import React, { SyntheticEvent, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Page from "../../components/Page";
import "./index.css";
import { Box, AlertColor } from "@mui/material";
import { Form } from "react-bootstrap";
import config, { getConfigForRegion, Regions } from "../../config";
import CustomSnackbar from "../../components/CustomSnackbar";
import CustomButton from "../../components/CustomButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useAuth } from "../../context/AuthContext";

const { ID_CREATE_NEW } = config?.app;
const HEADER_NAME = "";

interface FeatureData {
    id?: number;
    name: string;
    description: string;
}

const Feature = () => {
    const { featureId } = useParams();
    const navigate = useNavigate();
    const { isAuthenticating, headersWithAuth } = useAuth();
    const isCreatingNew = featureId === ID_CREATE_NEW;

    const [initialFeatureData, setInitialFeatureData] = useState<FeatureData | undefined>(undefined);
    const [updatedFeatureData, setUpdatedFeatureData] = useState<FeatureData>({
        name: "",
        description: ""
    });
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState<AlertColor>("success");
    const [isLoading, setIsLoading] = useState(false);

    const currentConfig = getConfigForRegion(Regions.Canada);

    const handleSnackbarClose = (event: SyntheticEvent<any, Event> | Event, reason?: string) => {
        if (reason === "clickaway") {
            return;
        }
        setOpenSnackbar(false);
    };

    const isFeatureChanged = (): boolean => {
        if (!initialFeatureData) return true;
        return JSON.stringify(initialFeatureData) !== JSON.stringify(updatedFeatureData);
    };

    const isDataValid = (): boolean => {
        const featureName = updatedFeatureData?.name || "";
        return featureName.trim() !== "";
    };

    useEffect(() => {
        const fetchFeature = async () => {
            if (!isCreatingNew && featureId) {
                try {
                    setIsLoading(true);
                    const response = await axios.post(
                        currentConfig.api.LICENSE_FEATURES,
                        {},
                        headersWithAuth
                    );
                    const features = response.data || [];
                    const feature = features.find((f: any) => f.id.toString() === featureId);
                    
                    if (feature) {
                        const featureData = {
                            id: feature.id,
                            name: feature.name,
                            description: feature.description || ""
                        };
                        setUpdatedFeatureData(featureData);
                        setInitialFeatureData(featureData);
                    }
                } catch (error) {
                    console.error("Error fetching feature:", error);
                    setSnackbarMessage("Failed to load feature");
                    setSnackbarSeverity("error");
                    setOpenSnackbar(true);
                } finally {
                    setIsLoading(false);
                }
            }
        };

        fetchFeature();
    }, [featureId, isCreatingNew]);

    const handleUpdate = (e: any, field: keyof FeatureData) => {
        const value = e.target.value;
        setUpdatedFeatureData((prev) => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSubmit = async (e: any) => {
        e.preventDefault();

        if (!isDataValid()) {
            setSnackbarMessage("Please fill in all required fields");
            setSnackbarSeverity("error");
            setOpenSnackbar(true);
            return;
        }

        try {
            setIsLoading(true);
            const endpoint = isCreatingNew 
                ? currentConfig.api.CREATE_LICENSE_FEATURE 
                : currentConfig.api.UPDATE_LICENSE_FEATURE;

            const payload = isCreatingNew
                ? {
                      name: updatedFeatureData.name,
                      description: updatedFeatureData.description
                  }
                : {
                      id: updatedFeatureData.id,
                      name: updatedFeatureData.name,
                      description: updatedFeatureData.description
                  };

            console.log("Sending feature request:", { endpoint, payload });
            const response = await axios.post(endpoint, payload, headersWithAuth);
            console.log("Feature response:", response.data);

            const errorCode = response?.data?.errorCode || (Array.isArray(response?.data) && response?.data[0]?.errorCode);
            const errorMessage = response?.data?.message || (Array.isArray(response?.data) && response?.data[0]?.message);

            if (errorCode || response?.status >= 400) {
                throw new Error(errorMessage || `Request failed with status ${response?.status}`);
            }

            setSnackbarMessage(
                isCreatingNew ? "Feature created successfully!" : "Feature updated successfully!"
            );
            setSnackbarSeverity("success");
            setOpenSnackbar(true);

            setTimeout(() => {
                navigate("/home", { state: { fromEditPage: true } });
            }, 1000);
        } catch (error: any) {
            console.error("Error saving feature:", error);
            const errorMsg = error?.response?.data?.message 
                || error?.response?.data?.error
                || error?.message 
                || "Failed to save feature";
            setSnackbarMessage(errorMsg);
            setSnackbarSeverity("error");
            setOpenSnackbar(true);
        } finally {
            setIsLoading(false);
        }
    };

    const pageTitle = isCreatingNew ? "Create Feature" : "Edit Feature";
    const name = updatedFeatureData?.name || "";
    const description = updatedFeatureData?.description || "";

    return (
        <Page name={HEADER_NAME} isAuthenticating={isAuthenticating}>
            <Box className="home-container py-3">
                <Box sx={{ padding: 2 }}>
                    <h1 data-cy={`pageTitle${featureId ? `-${featureId}` : ""}`}>{pageTitle}</h1>
                    <hr />
                    <Form onSubmit={handleSubmit}>
                        <Form.Group className="mb-3" controlId="formName">
                            <Form.Label>Feature Name *</Form.Label>
                            <Form.Control
                                type="text"
                                value={name}
                                onChange={(e: any) => handleUpdate(e, "name")}
                                required
                                data-cy="feature-name-input"
                            />
                            <small
                                style={{
                                    fontStyle: "italic",
                                    color: "grey",
                                    fontSize: "0.8em"
                                }}
                            >
                                Feature name is required
                            </small>
                        </Form.Group>
                        <Form.Group className="mb-3" controlId="formDescription">
                            <Form.Label>Description</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={description}
                                onChange={(e: any) => handleUpdate(e, "description")}
                                data-cy="feature-description-input"
                            />
                        </Form.Group>

                        <Box display="flex" gap={2}>
                            <CustomButton
                                size="medium"
                                type="button"
                                isLoading={false}
                                variant="outlined"
                                color="primary"
                                text="Go Back"
                                icon={ArrowBackIcon}
                                onClick={() => navigate(-1)}
                            />

                            <CustomButton
                                size="medium"
                                type="submit"
                                disabled={!isFeatureChanged() || !isDataValid() || isLoading}
                                isLoading={isLoading}
                                variant="contained"
                                color="primary"
                                text="Submit"
                                dataCy="submit-feature-button"
                            />
                        </Box>
                    </Form>
                </Box>
            </Box>

            <CustomSnackbar
                open={openSnackbar}
                handleClose={handleSnackbarClose}
                message={snackbarMessage}
                severity={snackbarSeverity}
            />
        </Page>
    );
};

export default Feature;
